// Pure rules behind the catering screens (Stage 2B). The server is the source
// of truth; these mirror it so the screen can show totals and the right
// buttons before a round trip. Tested in cateringLogic.test.ts.
import type { BookingAction, BookingInput, BookingStatus, BookingTotals, CateringBooking, KitchenEdit, NeedChange, PaymentKind, PricingMode } from './cateringTypes'
import { CBT } from './cateringStrings'
import type { PlanOrderType } from './planTypes'

export function bookingMoney(v: number): number {
  return Math.round(v * 100) / 100
}

export interface DraftLine {
  product_id: string
  name: string
  image_url?: string | null
  qty: number
  menu_price: number
  /** Special price; null = menu price. */
  special_price: number | null
  note: string
}

export interface DraftExtraLine {
  stock_item_id: string
  name: string
  unit: string
  qty: number
  is_returnable: boolean
}

export interface BookingDraft {
  customer_name: string
  customer_phone: string
  event_name: string
  event_date: string
  event_time: string
  venue: string
  guest_count: string
  order_type: PlanOrderType
  discount_amount: string
  discount_note: string
  notes: string
  /** Per dish, or per guest (guests × price_per_guest; dishes not charged). */
  pricing_mode: PricingMode
  /** As typed; '' = not set. Kept when switching to per dish so switching
   *  back restores it. */
  price_per_guest: string
  lines: DraftLine[]
  extras: DraftExtraLine[]
}

/** A new booking starts from the business's default pricing (Settings). */
export function emptyDraft(eventDate: string, pricing: PricingMode = 'per_dish'): BookingDraft {
  return {
    customer_name: '', customer_phone: '', event_name: '', event_date: eventDate, event_time: '', venue: '', guest_count: '',
    order_type: 'dine_in', discount_amount: '', discount_note: '', notes: '', pricing_mode: pricing, price_per_guest: '', lines: [], extras: [],
  }
}

export function draftFromBooking(b: CateringBooking): BookingDraft {
  return {
    customer_name: b.customer_name,
    customer_phone: b.customer_phone ?? '',
    event_name: b.event_name ?? '',
    event_date: b.event_date,
    event_time: b.event_time ?? '',
    venue: b.venue ?? '',
    guest_count: b.guest_count != null ? String(b.guest_count) : '',
    order_type: b.order_type,
    discount_amount: b.totals.discount > 0 ? String(b.totals.discount) : '',
    discount_note: b.discount_note ?? '',
    notes: b.notes ?? '',
    pricing_mode: b.pricing_mode === 'per_guest' ? 'per_guest' : 'per_dish',
    price_per_guest: b.price_per_guest != null ? String(b.price_per_guest) : '',
    lines: (b.lines ?? []).map((l) => ({
      product_id: l.product_id, name: l.name, image_url: l.image_url, qty: l.qty, menu_price: l.menu_price,
      special_price: l.unit_price !== l.menu_price ? l.unit_price : null, note: l.note ?? '',
    })),
    extras: (b.extras ?? []).map((x) => ({ ...x })),
  }
}

/** Adds a dish. A new dish starts at `qty` portions (defaultPortions: the
 *  guest count when set); tapping a dish already on the list adds one. */
export function addLine(lines: DraftLine[], p: { product_id: string; name: string; image_url?: string | null; price: number }, qty = 1): DraftLine[] {
  const i = lines.findIndex((l) => l.product_id === p.product_id)
  if (i >= 0) return lines.map((l, j) => (j === i ? { ...l, qty: l.qty + 1 } : l))
  const start = Number.isFinite(qty) && qty >= 1 ? Math.round(qty) : 1
  return [...lines, { product_id: p.product_id, name: p.name, image_url: p.image_url, qty: start, menu_price: p.price, special_price: null, note: '' }]
}

/** The guest count typed in the form; null = not set (empty, 0, letters). */
export function guestsOf(guestCount: string): number | null {
  const n = Number(guestCount.trim())
  return Number.isInteger(n) && n > 0 ? n : null
}

/** Portions a newly added dish starts at: one per guest, else 1. */
export const defaultPortions = (guestCount: string) => guestsOf(guestCount) ?? 1

/** The dish is made for exactly the guest count ("= guests" chip). */
export const matchesGuests = (l: Pick<DraftLine, 'qty'>, guestCount: string) => {
  const g = guestsOf(guestCount)
  return g != null && l.qty === g
}

export interface GuestsOffer {
  guests: number
  productIds: string[]
}

/** Guests changed from `before` to `after`: the dishes that were made for
 *  the old count, offered (never applied silently) to move to the new one. */
export function guestsChangeOffer(lines: DraftLine[], before: string, after: string): GuestsOffer | null {
  const from = guestsOf(before)
  const to = guestsOf(after)
  if (from == null || to == null || from === to) return null
  const productIds = lines.filter((l) => l.qty === from).map((l) => l.product_id)
  return productIds.length > 0 ? { guests: to, productIds } : null
}

export function applyGuestsOffer(lines: DraftLine[], offer: GuestsOffer): DraftLine[] {
  const ids = new Set(offer.productIds)
  return lines.map((l) => (ids.has(l.product_id) ? { ...l, qty: offer.guests } : l))
}

/** Changes one dish line. Portions are whole numbers; zero keeps the line
 *  (with its special price and note) — a cleared box is still being typed
 *  in. Only removeLine takes a dish off. */
export function setLine(lines: DraftLine[], productId: string, patch: Partial<DraftLine>): DraftLine[] {
  const p = patch.qty === undefined ? patch : { ...patch, qty: Math.max(0, Math.round(Number.isFinite(patch.qty) ? patch.qty : 0)) }
  return lines.map((l) => (l.product_id === productId ? { ...l, ...p } : l))
}

export function removeLine(lines: DraftLine[], productId: string): DraftLine[] {
  return lines.filter((l) => l.product_id !== productId)
}

/** Guests set and a dish made for a different number: "Chicken Karahi is for
 *  200" — a hint only, some dishes rightly differ (naan, drinks). */
export function portionHints(d: Pick<BookingDraft, 'guest_count' | 'lines'>): { guests: number; lines: { name: string; qty: number }[] } | null {
  const guests = parseInt(d.guest_count, 10)
  if (!Number.isFinite(guests) || guests <= 0) return null
  const lines = d.lines.filter((l) => l.qty > 0 && l.qty !== guests).map((l) => ({ name: l.name, qty: l.qty }))
  return lines.length > 0 ? { guests, lines } : null
}

/** True once the editor holds something other than what it opened with. */
export const draftDirty = (now: BookingDraft, start: BookingDraft) => JSON.stringify(now) !== JSON.stringify(start)

export const unitPriceOf = (l: DraftLine) => (l.special_price != null ? l.special_price : l.menu_price)

function num(s: string): number {
  const n = Number(s.trim())
  return Number.isFinite(n) ? n : 0
}

/** The price per guest typed; null = not a usable price (empty, zero, letters). */
export function pricePerGuestOf(text: string): number | null {
  return parseAmount(text)
}

/** What the food comes to before the discount — bookingSubtotal on the
 *  server: guests × price per guest, or each dish's portions × its price. */
export function draftSubtotal(d: Pick<BookingDraft, 'pricing_mode' | 'price_per_guest' | 'guest_count' | 'lines'>): number {
  if (d.pricing_mode === 'per_guest') return bookingMoney((guestsOf(d.guest_count) ?? 0) * (pricePerGuestOf(d.price_per_guest) ?? 0))
  return bookingMoney(d.lines.reduce((s, l) => s + l.qty * unitPriceOf(l), 0))
}

/** Same arithmetic as computeBookingTotals on the server. */
export function draftTotals(d: BookingDraft, paid = 0): BookingTotals {
  const subtotal = draftSubtotal(d)
  const discount = bookingMoney(Math.min(Math.max(num(d.discount_amount), 0), subtotal))
  const total = bookingMoney(subtotal - discount)
  return { subtotal, discount, total, paid: bookingMoney(paid), balance: bookingMoney(total - paid) }
}

// ── the quote ──────────────────────────────────────────────────────────────

export type { PricingMode }

export type QuoteRow =
  | { kind: 'dishes'; dishes: number; portions: number; amount: number }
  /** "250 guests × Rs 2,400 = Rs 600,000" */
  | { kind: 'perGuest'; guests: number; price: number; amount: number }

export interface QuoteSummary {
  mode: PricingMode
  /** What is charged, one row per line on the quote card. */
  rows: QuoteRow[]
  /** Per guest: the dishes the kitchen cooks, included in the price. */
  included: { dishes: number; portions: number } | null
  /** Equipment & packaging going out — listed, not charged. */
  extras: { name: string; qty: number; unit: string }[]
  totals: BookingTotals
}

/** Everything the quote card shows, from one place, for either pricing mode.
 *  Same arithmetic as draftTotals / the server. */
export function quoteSummary(d: BookingDraft, opts: { paid?: number } = {}): QuoteSummary {
  const mode = d.pricing_mode
  const totals = draftTotals(d, opts.paid ?? 0)
  const dishes = { dishes: d.lines.length, portions: d.lines.reduce((s, l) => s + l.qty, 0) }
  return {
    mode,
    rows: mode === 'per_guest'
      ? [{ kind: 'perGuest', guests: guestsOf(d.guest_count) ?? 0, price: pricePerGuestOf(d.price_per_guest) ?? 0, amount: totals.subtotal }]
      : [{ kind: 'dishes', ...dishes, amount: totals.subtotal }],
    included: mode === 'per_guest' && dishes.dishes > 0 ? dishes : null,
    extras: d.extras.filter((x) => x.qty > 0).map((x) => ({ name: x.name, qty: x.qty, unit: x.unit })),
    totals,
  }
}

/** A "deposit taken now" that would be more than the booking's total. */
export function depositTooBig(deposit: number | null, total: number): boolean {
  return deposit != null && deposit > total + 0.005
}

/** The editor's "More details" starts open when any of them is filled in. */
export function moreDetailsSet(d: Pick<BookingDraft, 'event_time' | 'venue' | 'event_name' | 'order_type'>): boolean {
  return Boolean(d.event_time.trim() || d.venue.trim() || d.event_name.trim() || d.order_type !== 'dine_in')
}

/** "Extras" starts open when it holds anything. */
export function extrasSet(d: Pick<BookingDraft, 'extras' | 'discount_amount' | 'discount_note'>): boolean {
  return d.extras.length > 0 || num(d.discount_amount) > 0 || Boolean(d.discount_note.trim())
}

export type DraftProblem = 'needCustomer' | 'needDate' | 'needDish' | 'needQty' | 'needGuests' | 'needPricePerGuest' | 'discountTooBig'

export function draftProblem(d: BookingDraft): DraftProblem | null {
  if (!d.customer_name.trim()) return 'needCustomer'
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.event_date)) return 'needDate'
  if (d.lines.length === 0) return 'needDish'
  if (d.lines.some((l) => !(l.qty > 0))) return 'needQty'
  // Per guest (server booking_guests_required / booking_price_per_guest_required).
  if (d.pricing_mode === 'per_guest' && guestsOf(d.guest_count) == null) return 'needGuests'
  if (d.pricing_mode === 'per_guest' && pricePerGuestOf(d.price_per_guest) == null) return 'needPricePerGuest'
  if (num(d.discount_amount) > draftSubtotal(d) + 0.005) return 'discountTooBig'
  return null
}

/** Saving an existing booking would change what it charges because its
 *  pricing was switched: "This changes the total from Rs A to Rs B". Only
 *  for a booking the customer has agreed (confirmed or delivered). */
export function pricingSwitchChange(
  b: Pick<CateringBooking, 'status' | 'pricing_mode' | 'totals'> | null, d: BookingDraft,
): { from: number; to: number } | null {
  if (!b || (b.status !== 'confirmed' && b.status !== 'delivered')) return null
  if ((b.pricing_mode ?? 'per_dish') === d.pricing_mode) return null
  const to = draftTotals(d).total
  return Math.abs(to - b.totals.total) > 0.005 ? { from: b.totals.total, to } : null
}

/** The tenant's default pricing (Settings → kitchen_flow.catering_pricing).
 *  Missing or unreadable = per dish. */
export const CATERING_PRICING_KEY = 'kitchen_flow.catering_pricing'
export function parseCateringPricing(settings: Record<string, unknown> | undefined): PricingMode {
  const v = settings?.[CATERING_PRICING_KEY] as { mode?: unknown } | null | undefined
  return v && typeof v === 'object' && v.mode === 'per_guest' ? 'per_guest' : 'per_dish'
}

/**
 * Whether the booking form offers the Per head / Per dish switch: only for a
 * business that prices per guest, or a booking already priced per guest (so
 * it can be switched back). A per-dish business never sees it.
 */
export function showPricingSwitch(businessDefault: PricingMode, bookingMode: PricingMode, savedMode?: PricingMode | null): boolean {
  return businessDefault === 'per_guest' || bookingMode === 'per_guest' || savedMode === 'per_guest'
}

const opt = (s: string) => (s.trim() ? s.trim() : undefined)

export function bookingInputFrom(d: BookingDraft, confirm = false): BookingInput {
  const guests = parseInt(d.guest_count, 10)
  return {
    customer_name: d.customer_name.trim(),
    customer_phone: opt(d.customer_phone),
    event_name: opt(d.event_name),
    event_date: d.event_date,
    event_time: opt(d.event_time),
    venue: opt(d.venue),
    guest_count: Number.isFinite(guests) && guests >= 0 ? guests : undefined,
    order_type: d.order_type,
    discount_amount: bookingMoney(Math.max(num(d.discount_amount), 0)),
    discount_note: opt(d.discount_note),
    notes: opt(d.notes),
    lines: d.lines.map((l) => ({
      // Always send the charged price: left out, the server would fall back to
      // a price, and the booking must charge exactly what this screen shows.
      product_id: l.product_id, qty: Math.round(l.qty), unit_price: bookingMoney(unitPriceOf(l)),
      ...(l.note.trim() ? { note: l.note.trim() } : {}),
    })),
    extras: d.extras.filter((x) => x.qty > 0).map((x) => ({ stock_item_id: x.stock_item_id, qty: x.qty })),
    pricing_mode: d.pricing_mode,
    // Sent whenever typed (also per dish), so switching back keeps it.
    ...(pricePerGuestOf(d.price_per_guest) != null ? { price_per_guest: bookingMoney(pricePerGuestOf(d.price_per_guest) ?? 0) } : {}),
    ...(confirm ? { confirm: true } : {}),
  }
}

// ── roles & states ─────────────────────────────────────────────────────────

/** Book, change and take money: admin and manager (backend cateringWrite). */
export const canWriteBookings = (role: string) => role === 'admin' || role === 'manager'
/** Send to kitchen: + kitchen manager (backend cateringPlan). */
export const canSendToKitchen = (role: string) => canWriteBookings(role) || role === 'kitchen_manager'

export const bookingEditable = (s: BookingStatus) => s === 'enquiry' || s === 'confirmed' || s === 'delivered'
export const dishesEditable = (s: BookingStatus) => s === 'enquiry' || s === 'confirmed'

/** A kitchen plan that is still going (not cancelled by the kitchen). */
export const hasLivePlan = (b: Pick<CateringBooking, 'plan_id' | 'plan_status'>) => Boolean(b.plan_id) && b.plan_status !== 'cancelled'

/** The status button that is the main next step, or null when that step is
 *  elsewhere: a confirmed booking with no kitchen plan should go to the
 *  kitchen first, so "Mark delivered" stays secondary. */
export function primaryAction(b: Pick<CateringBooking, 'status' | 'totals' | 'plan_id' | 'plan_status'>): BookingAction | null {
  const first = bookingActions(b)[0]
  if (!first || first === 'cancel') return null
  if (first === 'deliver' && !hasLivePlan(b)) return null
  return first
}

/** Actions that ask "are you sure?" first: they can't be undone from here. */
export const actionNeedsConfirm = (a: BookingAction) => a === 'deliver' || a === 'close' || a === 'cancel'

/** Status buttons for a booking, first = the main next step. */
export function bookingActions(b: Pick<CateringBooking, 'status' | 'totals'>): BookingAction[] {
  switch (b.status) {
    case 'enquiry':
      return ['confirm', 'cancel']
    case 'confirmed':
      return ['deliver', 'cancel']
    case 'delivered':
      return ['close']
  }
  return []
}

/** Closing needs a zero balance (server: catering_balance_due). */
export const canClose = (t: BookingTotals) => Math.abs(t.balance) < 0.005

export function paymentKindsFor(status: BookingStatus, paid: number): PaymentKind[] {
  if (status === 'closed') return []
  if (status === 'cancelled') return paid > 0.005 ? ['refund'] : []
  const kinds: PaymentKind[] = ['deposit', 'payment']
  if (paid > 0.005) kinds.push('refund')
  return kinds
}

/** Default kind for a new payment: Deposit until one is taken, then Payment. */
export function defaultPaymentKind(kinds: PaymentKind[], payments: { kind: PaymentKind; voided_at?: string | null }[] | undefined): PaymentKind | undefined {
  const hasDeposit = (payments ?? []).some((p) => p.kind === 'deposit' && !p.voided_at)
  const want: PaymentKind = hasDeposit ? 'payment' : 'deposit'
  return kinds.includes(want) ? want : kinds[0]
}

/** A typed amount: "10,000" and "10 000" are 10000. null = not a usable
 *  amount (empty, zero, negative, letters, or more than 2 decimals). */
export function parseAmount(text: string): number | null {
  const t = text.replace(/[\s,]/g, '')
  if (!/^\d+(\.\d{1,2})?$|^\.\d{1,2}$/.test(t)) return null
  const n = Number(t)
  return Number.isFinite(n) && n > 0 && n <= 1e9 ? n : null
}

export type BookingFilter = 'upcoming' | 'unpaid' | 'past' | 'cancelled'

/** The list request for a tab. Upcoming: today on, still open. Unpaid: any
 *  date, money due, not cancelled (server unpaid=1). Past: before today,
 *  the last year. Cancelled: the last year. */
export function bookingListParams(f: BookingFilter, today: string): { from?: string; to?: string; status?: string; unpaid?: 1; limit: number } {
  const yearAgo = shiftDate(today, -365)
  switch (f) {
    case 'upcoming':
      return { from: today, status: 'enquiry,confirmed,delivered', limit: 500 }
    case 'unpaid':
      return { unpaid: 1, limit: 500 }
    case 'past':
      return { from: yearAgo, to: shiftDate(today, -1), status: 'enquiry,confirmed,delivered,closed', limit: 500 }
    case 'cancelled':
      return { from: yearAgo, status: 'cancelled', limit: 500 }
  }
}

function shiftDate(day: string, days: number): string {
  const [y, m, d] = day.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d + days))
  return dt.toISOString().slice(0, 10)
}

/** Same rules as bookingListParams, on whatever came back; order: upcoming
 *  and unpaid oldest event first, past and cancelled newest first. */
export function filterBookings(list: CateringBooking[], f: BookingFilter, today: string): CateringBooking[] {
  const out = list.filter((b) => {
    if (f === 'cancelled') return b.status === 'cancelled'
    if (b.status === 'cancelled') return false
    if (f === 'unpaid') return b.totals.balance > 0.005
    if (f === 'upcoming') return b.event_date >= today && b.status !== 'closed'
    return b.event_date < today
  })
  return f === 'upcoming' || f === 'unpaid' ? out : [...out].reverse()
}

export type BalanceBadge = { kind: 'due' | 'paid' | 'refund'; amount: number }

export function balanceBadge(t: BookingTotals): BalanceBadge {
  if (t.balance > 0.005) return { kind: 'due', amount: t.balance }
  if (t.balance < -0.005) return { kind: 'refund', amount: -t.balance }
  return { kind: 'paid', amount: 0 }
}

// ── plan changes (revise / send back) ──────────────────────────────────────

/** Pre-fill "send back" with everything that is now needed less. */
export function initialSendBack(decreases: NeedChange[]): Record<string, number> {
  return Object.fromEntries(decreases.map((d) => [d.stock_item_id, d.qty]))
}

export function sendBackLines(amounts: Record<string, number>): { stock_item_id: string; qty: number }[] {
  return Object.entries(amounts)
    .filter(([, q]) => q > 1e-6)
    .map(([stock_item_id, qty]) => ({ stock_item_id, qty: Math.round(qty * 1e5) / 1e5 }))
}

/** The name printed on invoices — same order as the server's reportsBrandName:
 *  Settings restaurant name, receipt business name, then the tenant's display
 *  name. '' = none set (the invoice then prints no name). */
export function invoiceBusinessName(settings: Record<string, unknown> | undefined): string {
  for (const k of ['restaurant_name', 'receipt_business_name', 'tenant_display_name']) {
    const v = settings?.[k]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return ''
}

// ── kitchen hand-off & concurrent edits (pilot fixes) ──────────────────────

/** "Send to kitchen" shows on a confirmed booking with no live plan: never
 *  sent, or the kitchen cancelled the plan it had (the server makes a new one). */
export function kitchenSendable(b: Pick<CateringBooking, 'status' | 'plan_id' | 'plan_status'>): boolean {
  return b.status === 'confirmed' && (!b.plan_id || b.plan_status === 'cancelled')
}

/** The save was refused because someone else saved the booking first. */
export function isBookingChanged(e: unknown): boolean {
  return (e as { code?: string } | null)?.code === 'booking_changed'
}

/** Batch 6: the save would set back numbers the kitchen changed on the plan.
 *  Returns those changes (from the refusal's data), or null for any other
 *  error. */
export function kitchenEditsOf(e: unknown): KitchenEdit[] | null {
  const err = e as { code?: string; payload?: { kitchen_edits?: KitchenEdit[] } } | null
  if (err?.code !== 'plan_changed_by_kitchen') return null
  return err.payload?.kitchen_edits ?? []
}

/** What the manager is asked before saving over the kitchen's edit, e.g.
 *  "The kitchen changed this plan: Chicken Karahi 10 → 12. Saving will set it
 *  back to 10." */
export function kitchenEditsText(edits: KitchenEdit[]): string {
  const q = (e: KitchenEdit, v: number) => (e.unit ? `${fmtEditQty(v)} ${e.unit}` : fmtEditQty(v))
  const changed = edits.map((e) => `${e.name} ${q(e, e.booking_qty)} → ${q(e, e.kitchen_qty)}`).join(', ')
  if (edits.length === 1) {
    const e = edits[0]
    return `${CBT.kitchenChanged(changed)} ${CBT.setItBack(Math.abs(e.new_qty - e.booking_qty) < 1e-6, q(e, e.new_qty))}`
  }
  return `${CBT.kitchenChanged(changed)} ${CBT.setThemBack(edits.map((e) => `${e.name} ${q(e, e.new_qty)}`).join(', '))}`
}

function fmtEditQty(v: number): string {
  return String(Math.round(v * 1000) / 1000)
}

// ── progress strip & the one next step (Batch 4 redesign) ──────────────────

export type BookingStep = 'enquiry' | 'confirmed' | 'kitchen' | 'delivered' | 'paid' | 'closed'
export type StepState = 'done' | 'now' | 'todo'

type StepBooking = Pick<CateringBooking, 'status' | 'totals' | 'plan_id' | 'plan_status' | 'event_date'>

const settled = (t: BookingTotals) => t.balance < 0.005

/** The strip: Confirmed → With the kitchen → Delivered → Paid → Closed, with
 *  Enquiry in front while it is one. Each step is done / now / to do; "now"
 *  is the first step not done. Paid can be done before Delivered (paid in
 *  advance) — it still shows done. A cancelled booking has no strip (null). */
export function bookingSteps(b: StepBooking): { step: BookingStep; state: StepState }[] | null {
  if (b.status === 'cancelled') return null
  const s = b.status
  const delivered = s === 'delivered' || s === 'closed'
  const done: Record<BookingStep, boolean> = {
    enquiry: s !== 'enquiry',
    confirmed: s !== 'enquiry',
    // "With the kitchen" is now from confirming until delivered (sent or
    // still to send — the next step says which); a booking delivered without
    // the kitchen (cooked off the system) has still passed it.
    kitchen: delivered,
    delivered,
    paid: s !== 'enquiry' && b.totals.total > 0.005 && settled(b.totals),
    closed: s === 'closed',
  }
  const steps: BookingStep[] = s === 'enquiry' ? ['enquiry', 'confirmed', 'kitchen', 'delivered', 'paid', 'closed'] : ['confirmed', 'kitchen', 'delivered', 'paid', 'closed']
  let nowSet = false
  return steps.map((step) => {
    if (done[step]) return { step, state: 'done' as const }
    if (!nowSet) {
      nowSet = true
      return { step, state: 'now' as const }
    }
    return { step, state: 'todo' as const }
  })
}

/** What the store has done for the plan's request: lines asked, lines with
 *  something sent. null = not known (no request, or not loaded). */
export interface StoreProgress {
  asked: number
  sent: number
}

export function storeProgressOf(lines: { requested_qty: number; issued_qty: number }[] | undefined): StoreProgress | null {
  if (!lines || lines.length === 0) return null
  return { asked: lines.length, sent: lines.filter((l) => l.issued_qty > 0).length }
}

export type NextKey =
  | 'confirm' | 'send' | 'resend' | 'withKitchen' | 'eventDone' | 'collect' | 'refund' | 'close' | 'closed' | 'cancelled' | 'cancelledRefund'
export type NextAction = 'confirm' | 'send' | 'deliver' | 'pay' | 'close'

export interface NextStep {
  key: NextKey
  /** The one main button; null = nothing to do here. */
  action: NextAction | null
  /** Money still to take (collect) or give back (refund / cancelledRefund). */
  amount?: number
  /** withKitchen: the event date, and what the store has sent if known. */
  date?: string
  store?: StoreProgress | null
}

/** The booking's single next step and why, from its state. `today` is the
 *  device's local day (yyyy-mm-dd). Mirrors bookingActions / primaryAction:
 *  a confirmed booking with no live plan goes to the kitchen first, unless
 *  the event has already passed (then it only needs marking delivered). */
export function nextStep(b: StepBooking, today: string, store: StoreProgress | null = null): NextStep {
  const t = b.totals
  switch (b.status) {
    case 'enquiry':
      return { key: 'confirm', action: 'confirm' }
    case 'confirmed': {
      const passed = b.event_date < today
      if (passed) return { key: 'eventDone', action: 'deliver' }
      if (!hasLivePlan(b)) return { key: b.plan_id ? 'resend' : 'send', action: 'send' }
      if (b.event_date === today) return { key: 'eventDone', action: 'deliver' }
      return { key: 'withKitchen', action: null, date: b.event_date, store }
    }
    case 'delivered':
      if (t.balance > 0.005) return { key: 'collect', action: 'pay', amount: t.balance }
      if (t.balance < -0.005) return { key: 'refund', action: 'pay', amount: -t.balance }
      return { key: 'close', action: 'close' }
    case 'closed':
      return { key: 'closed', action: null }
    case 'cancelled':
      return t.paid > 0.005 ? { key: 'cancelledRefund', action: 'pay', amount: t.paid } : { key: 'cancelled', action: null }
  }
}

/** Who may take the next step: status steps and money = writers; sending to
 *  the kitchen also the kitchen manager. */
export function canTakeNext(action: NextAction, role: string): boolean {
  return action === 'send' ? canSendToKitchen(role) : canWriteBookings(role)
}

export type ListChip = { kind: 'send' | 'resend' | 'confirm' | 'eventPassed' | 'close' | 'due' | 'refund' | 'withKitchen'; amount?: number }

/** The next-step chip on a bookings list row ("Send to kitchen", "Rs 46,900
 *  due", "Event passed — mark delivered"). null = nothing to show (closed,
 *  cancelled with nothing to refund). Money due shows while the kitchen has
 *  it, since that is the only thing left to chase. */
export function listChip(b: StepBooking, today: string): ListChip | null {
  const n = nextStep(b, today)
  switch (n.key) {
    case 'confirm': return { kind: 'confirm' }
    case 'send': return { kind: 'send' }
    case 'resend': return { kind: 'resend' }
    case 'eventDone': return b.event_date < today ? { kind: 'eventPassed' } : { kind: 'withKitchen' }
    case 'withKitchen': return b.totals.balance > 0.005 ? { kind: 'due', amount: b.totals.balance } : { kind: 'withKitchen' }
    case 'collect': return { kind: 'due', amount: n.amount }
    case 'refund':
    case 'cancelledRefund': return { kind: 'refund', amount: n.amount }
    case 'close': return { kind: 'close' }
  }
  return null
}

/** The deposit taken on the booking (first non-voided deposit), for the
 *  money card's "Paid (deposit 20 Oct, bank)" line. */
export function firstDeposit<P extends { kind: PaymentKind; voided_at?: string | null }>(payments: P[] | undefined): P | null {
  return (payments ?? []).find((p) => p.kind === 'deposit' && !p.voided_at) ?? null
}
